import PropTypes from "prop-types";
import { useEffect, useState } from "react";
// @mui
import {
  Alert,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
// auth
import { useAuthContext } from "../../auth/useAuthContext";
import { getSelectionTokenValue } from "../../auth/utils";
// components
import Iconify from "../../components/iconify";
// utils
import { getResolvedId } from "../../utils/apiRequestV2";

// ----------------------------------------------------------------------

WarehouseSelectionModal.propTypes = {
  open: PropTypes.bool,
  mode: PropTypes.oneOf(["login", "switch"]),
  onClose: PropTypes.func,
  onCancel: PropTypes.func,
};

// mode="login": mandatory, cannot be dismissed; calls select-warehouse.
// mode="switch": dismissible, cancelling keeps the current warehouse; calls switch-warehouse.
// onCancel (login mode) is only offered once the selection token has expired, so the user is not trapped.
export default function WarehouseSelectionModal({ open, mode = "login", onClose, onCancel }) {
  const { user, warehouses, selectionToken, selectWarehouse, switchWarehouse } = useAuthContext();

  const isSwitch = mode === "switch";
  const currentWarehouseId = isSwitch ? getResolvedId(user?.warehouseId) : null;
  // Switching uses the current session's refresh token; only login needs a live selection token.
  const canSelect = isSwitch || !!getSelectionTokenValue(selectionToken);

  const [selectedId, setSelectedId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (open) {
      setSelectedId(null);
      setError("");
    }
  }, [open]);

  const handleClose = () => {
    if (isSwitch && !submitting) onClose?.();
  };

  const handleConfirm = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError("");
    try {
      if (isSwitch) {
        await switchWarehouse(selectedId);
        onClose?.();
      } else {
        await selectWarehouse(selectedId);
      }
    } catch (err) {
      console.error(err);
      setError(err?.message || "Unable to select warehouse");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={!!open}
      onClose={isSwitch ? handleClose : undefined}
      disableEscapeKeyDown={!isSwitch}
    >
      <DialogTitle sx={{ pr: isSwitch ? 6 : 3 }}>
        {isSwitch ? "Switch Warehouse" : "Select Warehouse"}
        {isSwitch && (
          <IconButton
            aria-label="Close"
            onClick={handleClose}
            disabled={submitting}
            sx={{ position: "absolute", right: 12, top: 12 }}
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          {isSwitch
            ? "Choose the warehouse you want to work in."
            : "Your account has access to multiple warehouses. Choose one to continue."}
        </Typography>

        {!canSelect && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Your warehouse selection has expired. Please log in again.
          </Alert>
        )}

        {!!error && canSelect && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <List disablePadding>
          {(warehouses || []).map((warehouse) => {
            const isCurrent = !!currentWarehouseId && warehouse.id === currentWarehouseId;
            return (
              <ListItemButton
                key={warehouse.id}
                selected={selectedId === warehouse.id}
                disabled={!canSelect || submitting || isCurrent}
                onClick={() => setSelectedId(warehouse.id)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemAvatar>
                  <Avatar src={warehouse.imageURL} alt={warehouse.name}>
                    {warehouse.name?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={warehouse.name}
                  secondary={isCurrent ? "Current warehouse" : warehouse.role?.name}
                />
                {selectedId === warehouse.id && (
                  <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: "primary.main" }} />
                )}
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>

      <DialogActions>
        {isSwitch && (
          <Button variant="outlined" color="inherit" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
        )}
        {!isSwitch && !canSelect && onCancel && (
          <Button variant="outlined" color="inherit" onClick={onCancel}>
            Back to login
          </Button>
        )}
        <LoadingButton
          variant="contained"
          onClick={handleConfirm}
          loading={submitting}
          disabled={!canSelect || !selectedId}
        >
          {isSwitch ? "Switch" : "Continue"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
